<template>
  <div>
    <Card class="country-general bon-a-savoir-card">
      <div class="general-header">
        <div>
          <Icon
            type="md-bulb"
            :size="25"
            style="margin-right: 15px"
          />
          <strong>{{ $t('Bon à savoir') }}</strong>
        </div>
        <div v-if="(s2ec.user.is_admin || s2ec.user.is_superuser) && !editMode">
          <Icon
            type="md-create"
            size="20"
            class="pointer-cursor"
            @click.native="toggleEditMode"
          />
        </div>
        <div v-if="editMode && (s2ec.user.is_admin || s2ec.user.is_superuser)">
          <Icon
            type="md-close"
            size="20"
            class="pointer-cursor"
            @click.native="cancelEdit"
          />
        </div>
      </div>

      <div v-if="editMode">
        <div v-if="editableItems.length > 0">
          <Table
            :columns="editTableColumns"
            :data="editableItems"
            :show-header="true"
            stripe
          />
        </div>
        <div v-else class="empty-fact-type">
          <h4 class="text-center">{{ $t('No items to edit') }}</h4>
        </div>

        <div class="bon-a-savoir-actions">
          <Button
            type="default"
            @click="cancelEdit"
          >
            {{ $t('Cancel') }}
          </Button>
          <Button
            type="primary"
            :loading="loading"
            @click="saveItems"
          >
            {{ $t('Save') }}
          </Button>
        </div>
      </div>

      <div v-else-if="!editMode">
        <Table
          v-if="bonASavoirItems.length > 0"
          :columns="tableColumns"
          :data="bonASavoirItems"
          :show-header="true"
          stripe
        />

        <div
          v-else
          class="empty-fact-type"
        >
          <h4 class="text-center">
            {{ $t('No data') }}
          </h4>
        </div>
      </div>

      <Spin
        v-if="loading"
        fix
      />
    </Card>

    <!-- Markdown Editor Modal -->
    <Modal
      v-model="editorModalVisible"
      :title="$t('Edit Content')"
      width="900"
      :mask-closable="false"
      @on-ok="saveEditorModal"
      @on-cancel="cancelEditorModal"
    >
      <AppMarkdownEditor
        v-if="editorModalVisible"
        v-model="currentEditingContent"
        :editable="true"
        min-height="500px"
      />
    </Modal>
  </div>
</template>

<script>
import { getBonASavoirQry, updateBonASavoirQry } from "../../../services/countryInfo.service";
import AppMarkdownEditor from '@/components/common/AppMarkdownEditor';
import { marked } from 'marked';

export default {
    name: "BonASavoir",
    components: {
        AppMarkdownEditor
    },
    data() {
        return {
            loading: false,
            editMode: false,
            bonASavoirItems: [],
            editableItems: [],
            editorModalVisible: false,
            currentEditingContent: '',
            currentEditingIndex: null,
        }
    },
    created() {
        this.loadBonASavoir();
    },
    computed: {
        tableColumns() {
            return [
                {
                    title: this.$t('Title'),
                    key: 'titre',
                    width: 250,
                },
                {
                    title: this.$t('Content'),
                    key: 'contenu_md',
                    render: (h, params) => {
                        return h('div', {
                            domProps: {
                                innerHTML: this.renderMarkdown(params.row.contenu_md)
                            }
                        });
                    }
                }
            ];
        },
        editTableColumns() {
            return [
                {
                    title: this.$t('Title'),
                    key: 'titre',
                    width: 250,
                    render: (h, params) => {
                        return h('Input', {
                            props: {
                                value: params.row.titre,
                                placeholder: this.$t('Title')
                            },
                            on: {
                                'on-change': (event) => {
                                    this.$set(this.editableItems[params.index], 'titre', event.target.value);
                                },
                                input: (value) => {
                                    this.$set(this.editableItems[params.index], 'titre', value);
                                }
                            }
                        });
                    }
                },
                {
                    title: this.$t('Content'),
                    key: 'contenu_md',
                    render: (h, params) => {
                        return h('div', [
                            h('div', {
                                domProps: {
                                    innerHTML: this.renderMarkdown(params.row.contenu_md || '')
                                },
                                style: {
                                    maxHeight: '100px',
                                    overflow: 'auto',
                                    marginBottom: '8px',
                                    padding: '8px',
                                    background: '#f8f8f9',
                                    borderRadius: '4px',
                                    minHeight: '40px'
                                }
                            }),
                            h('Button', {
                                props: {
                                    size: 'small',
                                    type: 'primary',
                                    icon: 'md-create'
                                },
                                on: {
                                    click: () => {
                                        this.openEditorModal(params.index);
                                    }
                                }
                            }, this.$t('Edit Content'))
                        ]);
                    }
                }
            ];
        }
    },
    methods: {
        loadBonASavoir() {
            this.loading = true;
            getBonASavoirQry(this.$route.params.country)
                .then(res => {
                    this.bonASavoirItems = res.data.sort((a, b) => a.ordre - b.ordre);
                })
                .catch(() => {
                    this.$Message.error({
                        content: this.$t('Unable to retrieve bon à savoir'),
                        duration: 5
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        toggleEditMode() {
            this.editMode = true;

            // Clone the items for editing
            try {
                if (this.$lodash && this.$lodash.cloneDeep) {
                    this.editableItems = this.$lodash.cloneDeep(this.bonASavoirItems);
                } else {
                    this.editableItems = JSON.parse(JSON.stringify(this.bonASavoirItems));
                }
            } catch (e) {
                this.editableItems = [...this.bonASavoirItems];
            }
        },

        cancelEdit() {
            this.editMode = false;
            this.editableItems = [];
        },

        saveItems() {
            this.loading = true;

            // Prepare the data to send
            const dataToSend = this.editableItems.map(item => ({
                id: item.id,
                titre: item.titre,
                contenu_md: item.contenu_md,
                ordre: item.ordre,
                publie: item.publie
            }));

            console.log('Sending data:', dataToSend);

            updateBonASavoirQry(this.$route.params.country, dataToSend)
                .then(() => {
                    this.$Message.success({
                        content: this.$t('Successfully updated bon à savoir'),
                        duration: 3
                    });
                    // Reload the data
                    this.loadBonASavoir();
                    // Exit edit mode
                    this.cancelEdit();
                })
                .catch((error) => {
                    console.error('Error updating bon à savoir:', error);
                    this.$Message.error({
                        content: this.$t('Error updating bon à savoir'),
                        duration: 5
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        openEditorModal(index) {
            this.currentEditingIndex = index;
            this.currentEditingContent = this.editableItems[index].contenu_md || '';
            this.editorModalVisible = true;
        },

        saveEditorModal() {
            if (this.currentEditingIndex !== null) {
                this.$set(this.editableItems[this.currentEditingIndex], 'contenu_md', this.currentEditingContent);
            }
            this.cancelEditorModal();
        },

        cancelEditorModal() {
            this.editorModalVisible = false;
            this.currentEditingContent = '';
            this.currentEditingIndex = null;
        },

        renderMarkdown(markdown) {
            if (!markdown) return '';
            try {
                marked.setOptions({
                    breaks: true,
                    gfm: true
                });
                return marked(markdown);
            } catch (error) {
                console.error('Markdown rendering error:', error);
                return markdown;
            }
        }
    }
}
</script>

<style>
.bon-a-savoir-card {
  margin-top: 20px;
}

.general-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e8eaec;
}

.bon-a-savoir-edit-item {
  margin-bottom: 20px;
}

.bon-a-savoir-actions {
  margin-top: 20px;
  text-align: right;
}

.bon-a-savoir-actions button {
  margin-left: 10px;
}

.empty-fact-type {
  padding: 40px 20px;
}

.pointer-cursor {
  cursor: pointer;
}

/* Table content styling */
.bon-a-savoir-card .ivu-table td {
  vertical-align: center;
}

.bon-a-savoir-card .ivu-table p {
  margin: 0.5em 0;
}

.bon-a-savoir-card .ivu-table p:first-child {
  margin-top: 0;
}

.bon-a-savoir-card .ivu-table p:last-child {
  margin-bottom: 0;
}

.bon-a-savoir-card .ivu-table strong {
  font-weight: 600;
}

.bon-a-savoir-card .ivu-table a {
  color: #2d8cf0;
  text-decoration: none;
}

.bon-a-savoir-card .ivu-table a:hover {
  text-decoration: underline;
}

/* Edit mode table styling */
.bon-a-savoir-card .ivu-table .ivu-input-wrapper,
.bon-a-savoir-card .ivu-table .ivu-input {
  width: 100%;
  padding: 10px 0;
}

.bon-a-savoir-card .ivu-table textarea.ivu-input {
  min-height: 80px;
}

.bon-a-savoir-card input , .bon-a-savoir-card textarea {
  padding: 10px !important;
}
</style>
